import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Trash2, RotateCcw, Loader2, Clock } from 'lucide-react';
import { toast } from 'sonner';

export default function EventTrashDialog({ open, onOpenChange, onChanged }) {
  const [data, setData] = useState(null);
  const [acting, setActing] = useState(null);

  const load = useCallback(async () => {
    try { const r = await axios.get(`${API}/admin/events/trash`); setData(r.data); }
    catch { toast.error('No se pudo cargar la papelera'); }
  }, []);

  useEffect(() => { if (open) { setData(null); load(); } }, [open, load]);

  const restore = async (ev) => {
    setActing(ev.id);
    try {
      const r = await axios.post(`${API}/admin/events/trash/${ev.id}/restore`);
      toast.success(`"${ev.title}" restaurado (${r.data.registrations} inscripciones)`);
      await load(); onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'No se pudo restaurar'); } finally { setActing(null); }
  };

  const purge = async (ev) => {
    if (window.prompt(`Eliminar DEFINITIVAMENTE "${ev.title}" y sus ${ev.registrations_count} inscripciones. Escribe ELIMINAR:`) !== 'ELIMINAR') return;
    setActing(ev.id);
    try { await axios.delete(`${API}/admin/events/trash/${ev.id}`); toast.success('Eliminado definitivamente'); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'No se pudo eliminar'); } finally { setActing(null); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="event-trash-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Trash2 className="w-5 h-5 text-gray-600" /> Papelera de eventos</DialogTitle>
          <DialogDescription>Los eventos borrados se conservan 30 días con sus inscripciones y fotos. Después se eliminan automáticamente.</DialogDescription>
        </DialogHeader>
        {!data && <div className="py-8 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-gray-500" /></div>}
        {data?.count === 0 && <p className="py-8 text-center text-gray-500" data-testid="event-trash-empty">La papelera está vacía.</p>}
        <div className="space-y-2">
          {data?.events.map((ev) => (
            <div key={ev.id} className="rounded-lg border border-gray-200 p-3 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`trash-item-${ev.id}`}>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-gray-900 truncate">{ev.title}</p>
                <p className="text-xs text-gray-500">{ev.start_date} → {ev.end_date} · {ev.registrations_count} inscripciones · borrado por {ev.deleted_by}</p>
                <p className={`text-xs inline-flex items-center gap-1 mt-1 font-medium ${ev.days_left <= 5 ? 'text-red-600' : 'text-amber-700'}`}>
                  <Clock className="w-3 h-3" /> Se elimina en {ev.days_left} días
                </p>
              </div>
              <div className="flex gap-2 shrink-0">
                <Button size="sm" disabled={acting === ev.id} onClick={() => restore(ev)} className="h-10 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`trash-restore-${ev.id}`}>
                  {acting === ev.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <><RotateCcw className="w-4 h-4 mr-1" /> Restaurar</>}
                </Button>
                <Button size="sm" variant="outline" disabled={acting === ev.id} onClick={() => purge(ev)} className="h-10 border-red-300 text-red-700" data-testid={`trash-purge-${ev.id}`}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
